/** Editor-facing alias captions. Must match FlowV2SiblingRefs.FormatAliasDescription. */

export function defaultClassName(projectName) {
    return projectName ? `Default${projectName}` : null;
}

export function parseGeneratedClassFullName(classFullName) {
    if (!classFullName || !String(classFullName).startsWith('User.'))
        return null;
    const rest = String(classFullName).slice(5);
    const lastDot = rest.lastIndexOf('.');
    if (lastDot <= 0 || lastDot >= rest.length - 1)
        return null;
    return {
        projectName: rest.slice(0, lastDot),
        className: rest.slice(lastDot + 1),
    };
}

export function formatAliasDescription({ alias, projectName, className, isDefault } = {}) {
    const def = isDefault === true
        || (!!className && !!projectName && className === defaultClassName(projectName));
    let body;
    if (def || !className)
        body = projectName ? `alias for project ${projectName}` : 'project alias';
    else if (!projectName)
        body = `alias for class ${className}`;
    else
        body = `alias for class ${className} in project ${projectName}`;
    if (!alias)
        return body.charAt(0).toUpperCase() + body.slice(1);
    return `${alias}: ${body}`;
}

export function aliasDescriptionFromRow(row, alias) {
    const parsed = parseGeneratedClassFullName(alias?.ClassFullName);
    const projectName = row?.Name || parsed?.projectName || '';
    const className = parsed?.className || '';
    return formatAliasDescription({
        alias: alias?.Alias,
        projectName,
        className,
        isDefault: className === defaultClassName(projectName),
    });
}

/** Folder-tree / editor label: default class is "default", never Default{Project}. */
export function aliasClassLabel(projectName, className) {
    if (!className)
        return '';
    if (className === defaultClassName(projectName))
        return 'default';
    return className;
}

export function generatedClassFullName(projectName, className) {
    if (!projectName || !className)
        return '';
    return `User.${projectName}.${className}`;
}

export function classNameFromAlias(row, alias) {
    const parsed = parseGeneratedClassFullName(alias?.ClassFullName);
    return parsed?.className || defaultClassName(row?.Name) || '';
}

function addClassName(names, projectName, name, isDefault) {
    if (!name)
        return;
    const def = isDefault === true || name === defaultClassName(projectName);
    const prev = names.get(name);
    if (!prev || def)
        names.set(name, { name, isDefault: def, label: def ? 'default' : name });
}

/** Classes a sibling row can bind: payload, folder tree, then aliases + default. */
export function listClassesForRow(row, folderItems) {
    const projectName = row?.Name || '';
    const names = new Map();
    for (const c of row?.Classes || []) {
        addClassName(names, projectName, c?.Name || c?.name, c?.IsDefault || c?.isDefault);
    }
    const projectPath = row?.Path ? `${row.Path}/${row.Name}` : row?.Name;
    for (const item of folderItems || []) {
        if (item?.Type === 'Class' && item.Path === projectPath)
            addClassName(names, projectName, item.Name, false);
    }
    for (const alias of row?.Aliases || []) {
        const parsed = parseGeneratedClassFullName(alias?.ClassFullName);
        if (parsed?.className)
            addClassName(names, projectName, parsed.className, false);
    }
    addClassName(names, projectName, defaultClassName(projectName), true);
    return [...names.values()].sort((a, b) => {
        if (a.isDefault !== b.isDefault)
            return a.isDefault ? -1 : 1;
        return String(a.label).localeCompare(String(b.label));
    });
}

export function preferredUnusedClassName(row, folderItems) {
    const used = new Set(
        (row?.Aliases || [])
            .map((a) => parseGeneratedClassFullName(a?.ClassFullName)?.className)
            .filter(Boolean),
    );
    const classes = listClassesForRow(row, folderItems);
    return (classes.find((c) => !used.has(c.name)) || classes[0])?.name || defaultClassName(row?.Name) || '';
}
